import React, { useState } from 'react'
import { Card, Button, IconButton, Pill } from '../../ui'
import { CreateTaskModal } from '../../components/tasks/CreateTaskModal.jsx'
import { InlineManagerNote } from '../annotations/InlineManagerNote.jsx'

/**
 * Forward-look card 2: list of commitments with promote-to-task action.
 */
export function CommitmentListCard({
  tag = 'COMMIT',
  title = 'Specific commitments',
  items = [],
  onChange,
  onPromoteToTask,
  placeholder = 'What will you commit to? Be specific enough that you\'ll know if you did it.',
  readOnly = false,
  isClosed = false,
  me,
  reports = [],
  managerNote = '',
  onManagerNoteChange,
  onSuggestManagerNote,
  isManager = false,
  isReady = false,
  isShared = false,
}) {
  const [newText, setNewText] = useState('')
  const [promoteItem, setPromoteItem] = useState(null)

  function handleAdd() {
    if (!newText.trim()) return
    const newItem = {
      id: crypto.randomUUID(),
      text: newText.trim(),
      detail: '',
      promotedTaskId: null,
    }
    onChange?.([...items, newItem])
    setNewText('')
  }

  function handleRemove(id) {
    onChange?.(items.filter((item) => item.id !== id))
  }

  function handleUpdate(id, field, value) {
    onChange?.(items.map((item) => (item.id === id ? { ...item, [field]: value } : item)))
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleAdd()
    }
  }

  async function handleTaskCreated(task) {
    if (!promoteItem) return
    await onPromoteToTask?.(promoteItem.id, task)
    setPromoteItem(null)
  }

  function handlePromoteClick(item) {
    // If already promoted, don't re-promote
    if (item.promotedTaskId) return
    setPromoteItem(item)
  }

  return (
    <>
      <Card style={isClosed ? { background: 'var(--locked-bg)' } : undefined}>
        <div style={styles.container}>
          <span style={styles.tag}>{tag}</span>
          <h4 style={styles.title}>{title}</h4>

          {items.length === 0 && readOnly && (
            <span style={styles.muted}>None added</span>
          )}

          <div style={styles.list}>
            {items.map((item) => (
              <div key={item.id} style={styles.item}>
                <div style={styles.itemHeader}>
                  {readOnly ? (
                    <span style={styles.itemText}>{item.text}</span>
                  ) : (
                    <input
                      style={styles.itemTextInput}
                      value={item.text}
                      onChange={(e) => handleUpdate(item.id, 'text', e.target.value)}
                      disabled={readOnly}
                      aria-label="Commitment text"
                    />
                  )}
                  {!readOnly && (
                    <IconButton
                      icon="x"
                      size="sm"
                      onClick={() => handleRemove(item.id)}
                      aria-label="Remove commitment"
                    />
                  )}
                </div>

                {readOnly ? (
                  item.detail && <p style={styles.detailText}>{item.detail}</p>
                ) : (
                  <textarea
                    style={{ ...styles.textarea, background: 'transparent' }}
                    value={item.detail || ''}
                    onChange={(e) => handleUpdate(item.id, 'detail', e.target.value)}
                    placeholder="Add detail..."
                    disabled={readOnly}
                    rows={1}
                    aria-label="Commitment detail"
                  />
                )}

                <div style={styles.itemActions}>
                  {item.promotedTaskId ? (
                    <Pill tone="success">\u2192 Task created</Pill>
                  ) : !readOnly ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handlePromoteClick(item)}
                    >
                      Promote to task
                    </Button>
                  ) : null}
                </div>
              </div>
            ))}
          </div>

          {!readOnly && (
            <div style={styles.addRow}>
              <input
                style={styles.addInput}
                value={newText}
                onChange={(e) => setNewText(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={placeholder}
                aria-label="New commitment"
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={handleAdd}
                disabled={!newText.trim()}
              >
                Add
              </Button>
            </div>
          )}
          <InlineManagerNote
            value={managerNote}
            onChange={onManagerNoteChange}
            onSuggest={onSuggestManagerNote}
            isManager={isManager}
            isReady={isReady}
            isShared={isShared}
            isClosed={isClosed}
          />
        </div>
      </Card>

      {promoteItem && (
        <CreateTaskModal
          open={true}
          onClose={() => setPromoteItem(null)}
          onCreated={handleTaskCreated}
          defaultTitle={promoteItem.text}
          me={me}
          reports={reports}
        />
      )}
    </>
  )
}

const styles = {
  container: { display: 'flex', flexDirection: 'column', gap: '8px' },
  tag: {
    fontSize: '10px',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: 'var(--text-secondary)',
  },
  title: {
    fontSize: '13px',
    fontWeight: 600,
    color: 'var(--text-primary)',
    margin: 0,
  },
  list: { display: 'flex', flexDirection: 'column', gap: '8px' },
  item: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    padding: '8px',
    border: '1px solid var(--border)',
    borderRadius: '8px',
  },
  itemHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '8px',
  },
  itemText: {
    fontSize: '13px',
    fontWeight: 600,
    color: 'var(--text-primary)',
    flex: 1,
  },
  itemTextInput: {
    flex: 1,
    fontSize: '13px',
    fontWeight: 600,
    color: 'var(--text-primary)',
    background: 'transparent',
    border: 'none',
    outline: 'none',
    fontFamily: 'inherit',
  },
  textarea: {
    width: '100%',
    border: 'none',
    outline: 'none',
    color: 'var(--text-primary)',
    resize: 'vertical',
    lineHeight: 1.6,
    fontSize: '12px',
    fontFamily: 'inherit',
  },
  detailText: {
    fontSize: '12px',
    color: 'var(--text-secondary)',
    lineHeight: 1.6,
    margin: 0,
    whiteSpace: 'pre-wrap',
  },
  itemActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  addRow: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
  },
  addInput: {
    flex: 1,
    fontSize: '13px',
    color: 'var(--text-primary)',
    background: 'transparent',
    border: '1px solid var(--border)',
    borderRadius: '8px',
    padding: '6px 10px',
    outline: 'none',
    fontFamily: 'inherit',
  },
  muted: { fontSize: '12px', color: 'var(--text-secondary)' },
}
